import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { safeToString } from "_shared/_utilities/safe-to-string";
import Guid from "_shared/guids/guid";
import { Change, diffLines, diffWords } from "diff";
import { TextChangeJSON } from "work/proposals/redlining/__diff/_schema/text-change-schema";
import { IRedlineableField } from "work/proposals/redlining/field-redlines/field-redline";

export interface TextChangeCollectionJSON {
  changes: TextChangeJSON[];
}

export class TextChangeCollection {
  public static fromJSONArray(jsonArray: Array<TextChangeJSON> | null | undefined): TextChangeCollection | undefined {
    if (isNullOrUndefined(jsonArray)) return;
    const collection = new TextChangeCollection();
    jsonArray.forEach((json) => {
      collection.add(TextChange.fromJSON(json, collection));
    });
    if (collection.textChanges.length === 0) {
      throw new Error("TextChangeCollection has no changes in fromJSONArray.");
    }
    return collection;
  }

  public static fromRedlines<T extends IRedlineableField>(
    redlines: {
      originalEntry: T | null;
      revisedEntry: T | null;
      currentEntry: T | null | undefined;
    },
    options: { diffWords: boolean }
  ): TextChangeCollection | undefined {
    const { originalEntry, revisedEntry, currentEntry } = redlines;
    if (originalEntry === null && revisedEntry === null && !currentEntry) return;

    let { previous, next } = getPreviousAndNextFromEntries(revisedEntry, currentEntry, originalEntry);

    const diff = options.diffWords ? diffWords(previous, next) : diffLines(previous, next);
    const collection = new TextChangeCollection();
    diff.forEach((change) => {
      collection.add(new TextChange({ diff: change, parent: collection }));
    });
    if (collection.textChanges.length === 0) {
      throw new Error("TextChangeCollection has no changes in fromJSONArray.");
    }
    return collection;
  }

  public get textChanges(): TextChange[] {
    return [...this._textChanges];
  }

  public add(textChange: TextChange): void {
    this._textChanges.push(textChange);
  }
  public get allChangesResolved(): boolean {
    return this.textChanges.every((change) => change.isResolved);
  }
  public toJSON(): TextChangeCollectionJSON {
    return {
      changes: this.textChanges.map((change) => change.toJSON()),
    };
  }

  public constructor(textChanges: TextChange[] = []) {
    this._textChanges = [...textChanges];
  }

  protected _textChanges: TextChange[] = [];
}

export default class TextChange {
  public readonly id = Guid.generate();
  public readonly parent: TextChangeCollection;
  public readonly diff: Change;

  public get index(): number {
    return this.parent.textChanges.indexOf(this);
  }
  public get isResolved(): boolean {
    return !this.diff.added && !this.diff.removed;
  }
  public get previousChange(): TextChange | undefined {
    return this.parent.textChanges[this.index - 1];
  }
  public get nextChange(): TextChange | undefined {
    return this.parent.textChanges[this.index + 1];
  }
  public get isModified(): boolean {
    return (
      ((this.diff.removed && this.nextChange?.diff.added) || (this.previousChange?.diff.removed && this.diff?.added)) ??
      false
    );
  }
  public get isAdded(): boolean {
    return (this.diff.added && !this.previousChange?.diff.added && !this.previousChange?.diff.removed) ?? false;
  }
  public get isRemoved(): boolean {
    return (this.diff.removed && !this.previousChange?.diff.added && !this.previousChange?.diff.removed) ?? false;
  }

  constructor(params: { parent?: TextChangeCollection; diff: Change }) {
    this.parent = params.parent ?? new TextChangeCollection();
    this.diff = params.diff;
  }

  public accept(): TextChange[] {
    const updatedChanges = [...this.parent.textChanges];
    const previousIndex = this.index - 1;

    // resolve previous change
    if (this.isModified && this.previousChange?.isModified) {
      const newChange = new TextChange({
        parent: this.parent,
        diff: {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        },
      });
      updatedChanges.splice(previousIndex, 2, newChange);
    }

    // resolve next change
    if (this.isModified && this.nextChange?.isModified) {
      const nextChange = new TextChange({
        parent: this.parent,
        diff: {
          added: false,
          removed: false,
          value: this.nextChange.diff.value,
          count: this.nextChange.diff.count,
        },
      });
      updatedChanges.splice(this.index, 2, nextChange);
    }

    // resolve current change
    if (!this.isModified && this.isRemoved) {
      updatedChanges.splice(this.index, 1);
    }

    if (!this.isModified && this.isAdded) {
      const newChange = new TextChange({
        parent: this.parent,
        diff: {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        },
      });
      updatedChanges.splice(this.index, 1, newChange);
    }

    return updatedChanges;
  }

  public reject(): TextChange[] {
    const updatedChanges = [...this.parent.textChanges];
    const previousIndex = this.index - 1;

    // resolve previous change
    if (this.isModified && this.previousChange?.isModified) {
      const newChange = new TextChange({
        parent: this.parent,
        diff: {
          added: false,
          removed: false,
          value: this.previousChange?.diff.value,
          count: this.previousChange?.diff.count,
        },
      });
      updatedChanges.splice(previousIndex, 2, newChange);
    }

    // resolve next change
    if (this.isModified && this.nextChange?.isModified) {
      const nextChange = new TextChange({
        parent: this.parent,
        diff: {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        },
      });
      updatedChanges.splice(this.index, 2, nextChange);
    }

    // resolve current change
    if (!this.isModified && this.isRemoved) {
      const newChange = new TextChange({
        parent: this.parent,
        diff: {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        },
      });
      updatedChanges.splice(this.index, 1, newChange);
    }

    if (!this.isModified && this.isAdded) {
      updatedChanges.splice(this.index, 1);
    }

    return updatedChanges;
  }

  public editChange(newValue: string): TextChange[] {
    const updatedChanges = [...this.parent.textChanges];
    const previousIndex = this.index - 1;

    const newChange = new TextChange({
      parent: this.parent,
      diff: {
        added: false,
        removed: false,
        value: newValue,
        count: this.diff.count,
      },
    });

    if (this.isModified && this.previousChange?.isModified) {
      updatedChanges.splice(previousIndex, 2, newChange);
    } else if (this.isModified && this.nextChange?.isModified) {
      updatedChanges.splice(this.index, 2, newChange);
    } else if (!this.isModified) {
      updatedChanges.splice(this.index, 1, newChange);
    }

    return updatedChanges;
  }

  public toJSON(): TextChangeJSON {
    return {
      diff: this.diff,
      isResolved: this.isResolved,
    };
  }

  static fromJSON(json: TextChangeJSON, parent?: TextChangeCollection): TextChange {
    return new TextChange({ parent, diff: json.diff });
  }

  public clone(parent: TextChangeCollection): TextChange {
    return new TextChange({ parent, diff: this.diff });
  }
}

function getPreviousAndNextFromEntries<T extends IRedlineableField>(
  revisedEntry: T | null,
  currentEntry: T | null | undefined,
  originalEntry: T | null
) {
  let previous = "";
  let next = "";

  if (currentEntry === undefined) {
    previous = safeToString(originalEntry);
    next = safeToString(revisedEntry);
  } else {
    previous = safeToString(revisedEntry);
    next = safeToString(currentEntry);
  }

  return { previous, next };
}
